/**
 * `useAiChatThread` — shared hook for AI chat-bearing components.
 *
 * Wraps the load-latest + poll-while-trailing-user lifecycle introduced for
 * reactive-viz in ADR-0076. Spec: `atlas-meta/ai/SPECS/20260429_103025-ai-chat-thread-persistence-impl-plan.md` §5.1.
 *
 * **Why a hook.** Every chat-bearing surface in Console (reactive-viz, TanTable,
 * KPI Card, Visualization legacy, AI Provider authoring, Transform workbench,
 * Architect, Atlas Query, Help) had its own `useEffect` to fetch the latest
 * session. Most never gained the ADR-0076 polling-on-remount or "Thinking…"
 * indicator behavior. This hook owns the lifecycle so every adopter gets
 * consistent click-away/back behavior + a single place to evolve the pattern.
 *
 * **What the hook does NOT own:**
 * - `chatBusy` (active-mount send-in-progress) — surface-local; OR with
 *   `replyPending` for the composer disable rule.
 * - The send callback (`sendMessage`) — surface-specific orchestration.
 * - SSE streaming — owned by the surface's `sendMessage`. The hook polls only
 *   when the trailing message is a user role (i.e. an active send is either
 *   in flight here or finished server-side after a click-away).
 * - Per-message artifact extraction — the caller's `loadLatest` closure
 *   maps response → AiChatMessage; surface-specific fields stay surface-local.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Minimum shape the hook reads. Surfaces with richer per-message fields
 * (e.g. reactive-viz's `artifactSpec`, AI Provider's `suggestedPrompt`)
 * supply a generic `T extends AiChatMessage` and the hook stores them
 * untouched.
 */
export type AiChatMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
};

export type UseAiChatThreadArgs<T extends AiChatMessage = AiChatMessage> = {
  /**
   * Stable identifier the effect uses for cleanup + remount detection.
   * Typically a `${operationId}/${nodeId}/<surface>` string. Changing this
   * triggers cleanup + a fresh `loadLatest`.
   */
  threadKey: string;
  /**
   * Loader the hook calls on mount + on each poll iteration. Surfaces map
   * their `sessions/latest` response shape into `{ sessionId, messages,
   * latestToolPulse? }`. Throw `ApiRequestError` for HTTP errors; the hook
   * treats 404 as "no session yet" (sets `sessionId=null`, `messages=[]`,
   * no error).
   *
   * `latestToolPulse` is optional and Y-bundle-specific: surfaces whose
   * agents emit tool-call events (currently just reactive-viz) include the
   * current label so a remounted client sees the actual current step
   * instead of generic "Thinking…". Null/undefined when no run is in
   * flight or the agent isn't inside a tool call.
   */
  loadLatest: () => Promise<{
    sessionId: string | null;
    messages: T[];
    latestToolPulse?: string | null;
  }>;
  /**
   * Optional override for "still pending." Default: trailing message has
   * role === "user". Override only when a surface has a richer trailing-state
   * model (none today; reserved for correction-wiring / knowledge-proposal
   * pathways if they grow into pending semantics).
   */
  isPending?: (messages: T[]) => boolean;
  /**
   * When true, the hook does NOT load or poll. For surfaces that stay mounted
   * across route changes but only need chat state when "open" (HelpWidget
   * passes `disabled: !open`). Transition `true → false` triggers an
   * immediate `loadLatest`. Transition `false → true` clears any pending
   * poll timeout but leaves messages/sessionId in place so reopen has no
   * flicker. Defaults to `false`.
   */
  disabled?: boolean;
  /**
   * Optional creator for "Reset chat" UX. When provided, the hook returns
   * a `resetChat()` method that abandons the current session, creates a
   * fresh one via this callback, and clears local message state. Surfaces
   * that don't pass this get a no-op `resetChat`. The callback should hit
   * the surface's `POST .../sessions` endpoint and return the new session id.
   *
   * Why a separate callback rather than wiring it into `loadLatest`: reset
   * is rare and explicit; the hook should make it impossible to accidentally
   * trigger a session-create from the polling path.
   */
  createNewSession?: () => Promise<{ sessionId: string }>;
  /**
   * Optional aborter for "Cancel chat" UX (Y bundle / spec §10.1.b).
   * When provided, the hook returns a `cancelChat()` method that hits the
   * shared abort endpoint (`POST /tenant/ai/sessions/:sessionId/abort`) and
   * triggers an immediate `refresh()` so the indicator clears as soon as
   * the server's catch persists the "Canceled by you" message.
   *
   * Why a separate callback rather than the hook calling `apiFetch` directly:
   * keeps the hook free of API layer dependencies (testable, no fetch mocking
   * required) and lets each surface control auth headers, error handling,
   * etc. Surfaces that don't pass this get a no-op `cancelChat`.
   */
  cancelChat?: (sessionId: string) => Promise<void>;
};

export type UseAiChatThreadReturn<T extends AiChatMessage = AiChatMessage> = {
  sessionId: string | null;
  setSessionId: (id: string | null) => void;
  messages: T[];
  setMessages: React.Dispatch<React.SetStateAction<T[]>>;
  /** Derived: trailing message is `user` role (default `isPending`). */
  replyPending: boolean;
  /**
   * Manually re-trigger a load outside the poll loop. Surfaces with a
   * post-send reload pattern (e.g. AI Provider authoring) call this after
   * `sendMessage` succeeds. Resets `pollAttempts` to 0 so a fresh send
   * re-engages polling on the next iteration. No-op when `disabled`.
   */
  refresh: () => Promise<void>;
  /** GET-error message; null when load succeeded or 404'd. */
  error: string | null;
  /** Active poll attempt counter (0 when idle). Useful for diagnostics. */
  pollAttempts: number;
  /**
   * Current tool-call label as last surfaced by `loadLatest` (Y bundle /
   * migration 131). Surfaces with tool-using agents (reactive-viz) display
   * this when set so a remounted client sees the actual current step
   * instead of generic "Thinking…". Null when no run is in flight, when
   * the surface's loadLatest doesn't return this field, or when the agent
   * isn't currently inside a tool call.
   */
  latestToolPulse: string | null;
  /**
   * Abandon the current session, create a fresh one, and clear local message
   * state. No-op if `createNewSession` was not provided. Surfaces hook this
   * up to a "Reset chat" button so users can escape stuck threads (e.g.
   * post-restart-stranded sessions per spec §3.6.3) and start clean.
   */
  resetChat: () => Promise<void>;
  /**
   * Cancel the in-flight agent run for the current session (Y bundle /
   * spec §10.1.b). The Hub aborts the controller via the shared abort
   * endpoint; the handler's catch persists "Canceled by you" which the
   * next poll surfaces. After the abort fires, the hook triggers an
   * immediate `loadLatest` so the UI updates fast (typically within a
   * single render). No-op if `cancelChat` callback or `sessionId` is null.
   */
  cancelChat: () => Promise<void>;
};

/**
 * Public constants — exported so surface code (and Layer 2 primitives later)
 * can reference them without re-deriving the cap window. Mirror server-side
 * `AI_CHAT_HANDLER_TIMEOUT_MS` in atlas-hub: 90 × 2000 = 180_000 ms = 3 min.
 */
export const AI_CHAT_POLL_INTERVAL_MS = 2000;
export const AI_CHAT_POLL_MAX_ATTEMPTS = 90;

const defaultIsPending = <T extends AiChatMessage>(messages: T[]): boolean =>
  messages.length > 0 && messages[messages.length - 1].role === "user";

export function useAiChatThread<T extends AiChatMessage = AiChatMessage>(
  args: UseAiChatThreadArgs<T>
): UseAiChatThreadReturn<T> {
  const { threadKey, loadLatest, isPending, disabled, createNewSession, cancelChat } = args;
  const isPendingFn = isPending ?? defaultIsPending;

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<T[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [latestToolPulse, setLatestToolPulse] = useState<string | null>(null);

  // Refs preserve stable identity across renders. The effect closes over
  // `cancelledRef`, not `cancelled`, so unmount + threadKey change are
  // observable to in-flight fetches without re-running the effect on every
  // state update.
  const cancelledRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stash `loadLatest` + `isPendingFn` on a ref so a caller passing an
  // inline closure (the common case) doesn't restart the effect every
  // render. The effect re-reads `loadLatestRef.current` each iteration.
  const loadLatestRef = useRef(loadLatest);
  const isPendingFnRef = useRef(isPendingFn);
  loadLatestRef.current = loadLatest;
  isPendingFnRef.current = isPendingFn;

  const clearPollTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // The core load + schedule iteration. Returns whether a poll was scheduled
  // so `refresh()` can know it doesn't need to schedule again.
  const loadAndMaybeSchedule = useCallback(async (attempt: number): Promise<void> => {
    if (cancelledRef.current) return;
    try {
      const res = await loadLatestRef.current();
      if (cancelledRef.current) return;
      setSessionId(res.sessionId);
      setMessages(res.messages);
      setLatestToolPulse(res.latestToolPulse ?? null);
      setError(null);
      // Schedule the next iteration if the trailing message is still pending
      // and we haven't hit the cap. The next iteration runs in 2 s.
      if (
        isPendingFnRef.current(res.messages) &&
        attempt + 1 < AI_CHAT_POLL_MAX_ATTEMPTS
      ) {
        clearPollTimer();
        timerRef.current = setTimeout(() => {
          void loadAndMaybeSchedule(attempt + 1);
        }, AI_CHAT_POLL_INTERVAL_MS);
        setPollAttempts(attempt + 1);
      } else {
        // Trailing role is no longer user (assistant landed) OR cap reached.
        // Either way, polling stops.
        setPollAttempts(0);
      }
    } catch (e) {
      if (cancelledRef.current) return;
      // 404 is "no session yet" — common on first mount, not an error.
      const isNotFound =
        e !== null &&
        typeof e === "object" &&
        "status" in e &&
        (e as { status: unknown }).status === 404;
      if (isNotFound) {
        setSessionId(null);
        setMessages([]);
        setLatestToolPulse(null);
        setError(null);
        setPollAttempts(0);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }, [clearPollTimer]);

  // Mount + threadKey-change effect. Also re-runs when `disabled` toggles
  // because we read it inside the effect body.
  useEffect(() => {
    cancelledRef.current = false;
    clearPollTimer();
    setPollAttempts(0);

    if (disabled) {
      // Don't load or poll while disabled. Leave messages/sessionId as-is
      // so reopen (disabled true → false) shows the last-known state without
      // flicker; the false→true side of this transition handled below.
      return () => {
        cancelledRef.current = true;
        clearPollTimer();
      };
    }

    void loadAndMaybeSchedule(0);

    return () => {
      cancelledRef.current = true;
      clearPollTimer();
    };
  }, [threadKey, disabled, clearPollTimer, loadAndMaybeSchedule]);

  const refresh = useCallback(async (): Promise<void> => {
    if (disabled) return;
    clearPollTimer();
    setPollAttempts(0);
    await loadAndMaybeSchedule(0);
  }, [disabled, clearPollTimer, loadAndMaybeSchedule]);

  // Stash callbacks on refs so callers passing inline closures don't change
  // method identities every render (would re-trigger any useEffect that
  // depends on them).
  const createNewSessionRef = useRef(createNewSession);
  createNewSessionRef.current = createNewSession;
  const cancelChatRef = useRef(cancelChat);
  cancelChatRef.current = cancelChat;
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const resetChat = useCallback(async (): Promise<void> => {
    if (disabled) return;
    const create = createNewSessionRef.current;
    if (!create) return;
    clearPollTimer();
    setPollAttempts(0);
    setError(null);
    try {
      const res = await create();
      if (cancelledRef.current) return;
      setSessionId(res.sessionId);
      setMessages([]);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [disabled, clearPollTimer]);

  const cancelChatFn = useCallback(async (): Promise<void> => {
    if (disabled) return;
    const cancel = cancelChatRef.current;
    const sid = sessionIdRef.current;
    if (!cancel || !sid) return;
    try {
      await cancel(sid);
      if (cancelledRef.current) return;
      // Trigger an immediate poll so the "Canceled by you" message the Hub's
      // catch just persisted lands in local state without waiting up to 2 s
      // for the next scheduled poll iteration. We re-enter the poll loop
      // from attempt 0 — if for some reason the cancel didn't actually
      // unstick the trailing-user state (race, network), polling still
      // works the way it did before.
      clearPollTimer();
      setPollAttempts(0);
      await loadAndMaybeSchedule(0);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [disabled, clearPollTimer, loadAndMaybeSchedule]);

  const replyPending = isPendingFn(messages);

  return {
    sessionId,
    setSessionId,
    messages,
    setMessages,
    replyPending,
    refresh,
    error,
    pollAttempts,
    latestToolPulse,
    resetChat,
    cancelChat: cancelChatFn,
  };
}
